import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Activity, Phone, FileText, Clock, Download, RefreshCw, Loader2,
  TrendingUp, Users, Search, ChevronDown, ChevronUp,
} from 'lucide-react';

const PRESET_RANGES = [
  { label: 'Today', getValue: () => { const d = new Date().toISOString().slice(0,10); return [d, d]; }},
  { label: 'This Week', getValue: () => {
    const now = new Date(); const d = new Date(now); d.setDate(d.getDate() - d.getDay());
    return [d.toISOString().slice(0,10), now.toISOString().slice(0,10)];
  }},
  { label: 'This Month', getValue: () => {
    const now = new Date(); return [new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0,10), now.toISOString().slice(0,10)];
  }},
  { label: 'Last 30 Days', getValue: () => {
    const now = new Date(); const d = new Date(now); d.setDate(d.getDate() - 30);
    return [d.toISOString().slice(0,10), now.toISOString().slice(0,10)];
  }},
];

const CATEGORIES = [
  { value: 'all', label: 'All Departments' },
  { value: 'Sales', label: 'Sales' },
  { value: 'Customer Service', label: 'Customer Service' },
  { value: 'Mentor / Academics', label: 'Mentor / Academics' },
  { value: 'Business Development', label: 'Business Development' },
  { value: 'Other', label: 'Other / Support' },
];

function ScoreBadge({ score }) {
  const color = score >= 70 ? 'bg-emerald-500' : score >= 40 ? 'bg-amber-500' : score >= 15 ? 'bg-orange-500' : 'bg-red-500';
  return (
    <span className={`${color} text-white text-xs font-bold px-2.5 py-1 rounded-full`}>
      {score}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = 'text-foreground' }) {
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="p-2 rounded-lg bg-muted"><Icon className="h-5 w-5 text-muted-foreground" /></div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className={`text-xl font-bold ${color}`}>{value}</p>
          {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ProductivityReportPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => {
    const now = new Date(); return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0,10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0,10));
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('productivity_score');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedCat, setExpandedCat] = useState(null);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (category !== 'all') params.set('category', category);
      const res = await apiClient.get(`/reports/productivity?${params}`);
      setData(res.data);
    } catch { toast.error('Failed to load report'); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchReport(); }, []);

  const filtered = useMemo(() => {
    if (!data?.rows) return [];
    let rows = data.rows;
    if (category !== 'all') rows = rows.filter(r => r.category === category);
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(r => r.name.toLowerCase().includes(q) || r.role.toLowerCase().includes(q));
    }
    rows = [...rows].sort((a, b) => {
      const av = a[sortField] ?? 0, bv = b[sortField] ?? 0;
      return sortDir === 'desc' ? (bv > av ? 1 : -1) : (av > bv ? 1 : -1);
    });
    return rows;
  }, [data, category, search, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'desc' ? <ChevronDown className="h-3 w-3 inline" /> : <ChevronUp className="h-3 w-3 inline" />;
  };

  const handleExport = () => {
    if (!filtered.length) return;
    const headers = ['Name','Role','Category','Activities','Notes','Calls','Talk Time','Logins','Score'];
    const rows = filtered.map(r => [r.name, r.role, r.category, r.card_activities, r.notes_added, r.total_calls, r.talk_time_display, r.logins, r.productivity_score]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `productivity_report_${dateFrom}_${dateTo}.csv`;
    link.click();
    toast.success('CSV exported');
  };

  const totals = useMemo(() => {
    if (!filtered.length) return {};
    return {
      activities: filtered.reduce((s, r) => s + r.card_activities, 0),
      calls: filtered.reduce((s, r) => s + r.total_calls, 0),
      talkSec: filtered.reduce((s, r) => s + r.talk_time_seconds, 0),
      notes: filtered.reduce((s, r) => s + r.notes_added, 0),
    };
  }, [filtered]);

  const fmtTime = (s) => { const h = Math.floor(s/3600); const m = Math.floor((s%3600)/60); return h > 0 ? `${h}h ${m}m` : `${m}m`; };

  return (
    <div className="space-y-5" data-testid="productivity-report-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Productivity Report</h1>
          <p className="text-muted-foreground text-sm">Employee activities, call time, and engagement metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!filtered.length} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-1" /> Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card data-testid="report-filters">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            {PRESET_RANGES.map(pr => (
              <Button key={pr.label} variant="ghost" size="sm" className="text-xs"
                onClick={() => { const [f, t] = pr.getValue(); setDateFrom(f); setDateTo(t); }}
                data-testid={`preset-${pr.label.toLowerCase().replace(/\s/g,'-')}`}>
                {pr.label}
              </Button>
            ))}
            <div className="flex items-center gap-2 ml-2">
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-xs w-36" data-testid="date-from" />
              <span className="text-xs text-muted-foreground">to</span>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-xs w-36" data-testid="date-to" />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-8 text-xs w-44" data-testid="category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-2 text-muted-foreground" />
              <Input placeholder="Search name..." value={search} onChange={e => setSearch(e.target.value)}
                className="h-8 text-xs pl-8 w-44" data-testid="search-input" />
            </div>
            <Button size="sm" onClick={fetchReport} disabled={loading} data-testid="run-report-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-3.5 w-3.5 mr-1" /> Generate</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard icon={Users} label="People" value={filtered.length} sub={`of ${data.total_people} total`} />
          <StatCard icon={Activity} label="Card Activities" value={totals.activities} />
          <StatCard icon={Phone} label="Total Calls" value={totals.calls} />
          <StatCard icon={Clock} label="Talk Time" value={fmtTime(totals.talkSec || 0)} />
          <StatCard icon={FileText} label="Notes Added" value={totals.notes} />
        </div>
      )}

      {/* Category Summary Chips */}
      {data?.category_summaries?.length > 0 && category === 'all' && (
        <div className="flex flex-wrap gap-2">
          {data.category_summaries.map(cs => (
            <button key={cs.category}
              onClick={() => setExpandedCat(expandedCat === cs.category ? null : cs.category)}
              className={`text-xs border rounded-lg px-3 py-2 transition-all cursor-pointer
                ${expandedCat === cs.category ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:bg-muted border-border'}`}
              data-testid={`cat-chip-${cs.category.toLowerCase().replace(/[\s/]/g,'-')}`}>
              <span className="font-semibold">{cs.category}</span>
              <span className="ml-2 opacity-70">{cs.people} people</span>
              <span className="ml-2">{cs.total_activities} acts</span>
              <span className="ml-2">{cs.total_calls} calls</span>
              <span className="ml-2">{cs.total_talk_display}</span>
            </button>
          ))}
        </div>
      )}

      {/* Data Table */}
      <Card data-testid="productivity-table">
        <CardContent className="p-0">
          <ScrollArea className="max-h-[600px]">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="text-xs w-8">#</TableHead>
                  <TableHead className="text-xs cursor-pointer select-none" onClick={() => toggleSort('name')}>
                    Name <SortIcon field="name" />
                  </TableHead>
                  <TableHead className="text-xs">Role</TableHead>
                  <TableHead className="text-xs text-center cursor-pointer select-none" onClick={() => toggleSort('card_activities')}>
                    Activities <SortIcon field="card_activities" />
                  </TableHead>
                  <TableHead className="text-xs text-center cursor-pointer select-none" onClick={() => toggleSort('leads_touched')}>
                    Cards <SortIcon field="leads_touched" />
                  </TableHead>
                  <TableHead className="text-xs text-center cursor-pointer select-none" onClick={() => toggleSort('notes_added')}>
                    Notes <SortIcon field="notes_added" />
                  </TableHead>
                  <TableHead className="text-xs text-center cursor-pointer select-none" onClick={() => toggleSort('total_calls')}>
                    Calls <SortIcon field="total_calls" />
                  </TableHead>
                  <TableHead className="text-xs text-center cursor-pointer select-none" onClick={() => toggleSort('talk_time_seconds')}>
                    Talk Time <SortIcon field="talk_time_seconds" />
                  </TableHead>
                  <TableHead className="text-xs text-center cursor-pointer select-none" onClick={() => toggleSort('logins')}>
                    Logins <SortIcon field="logins" />
                  </TableHead>
                  <TableHead className="text-xs text-center cursor-pointer select-none" onClick={() => toggleSort('productivity_score')}>
                    Score <SortIcon field="productivity_score" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && !data ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-12 text-muted-foreground">No data found</TableCell></TableRow>
                ) : (
                  filtered.map((r, i) => (
                    <TableRow key={r.user_id} className={expandedCat && r.category !== expandedCat ? 'opacity-30' : ''}>
                      <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                      <TableCell className="text-sm font-medium">{r.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px] font-normal">{r.role?.replace(/_/g,' ')}</Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm font-semibold">{r.card_activities || '-'}</TableCell>
                      <TableCell className="text-center text-sm">{r.leads_touched || '-'}</TableCell>
                      <TableCell className="text-center text-sm">{r.notes_added || '-'}</TableCell>
                      <TableCell className="text-center text-sm font-semibold">{r.total_calls || '-'}</TableCell>
                      <TableCell className="text-center text-sm">{r.talk_time_seconds > 0 ? r.talk_time_display : '-'}</TableCell>
                      <TableCell className="text-center text-sm">{r.logins || '-'}</TableCell>
                      <TableCell className="text-center"><ScoreBadge score={r.productivity_score} /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
